import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Package, Inbox, CheckCircle2, UserCircle, AlertTriangle, Layers, DollarSign, ShoppingBag, Truck, CreditCard } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { getCasillero } from '../api';
import EstadoBadge from './EstadoBadge';
import PaqueteDetalleModal from './PaqueteDetalleModal';
import PrealertaModal from './PrealertaModal';
import PerfilModal from './PerfilModal';
import ConsolidadoDetalleModal from './ConsolidadoDetalleModal';
import DashboardCliente from './DashboardCliente';
import CompraAsistidaCliente from './CompraAsistidaCliente';
import PuertaPuertaCliente from './PuertaPuertaCliente';
import PagoPayphoneLiquidacion from './PagoPayphoneLiquidacion';

const PAIS_LABEL = { usa: '\u{1f1fa}\u{1f1f8} Miami', china: '\u{1f1e8}\u{1f1f3} Shenzhen' };

function PaqueteCard({ paquete, onClick }) {
  return (
    <div className="card card--compact card--clickable" onClick={onClick}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--spacing-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Package size={16} style={{ color: 'var(--color-text-secondary)' }} />
          <strong>{paquete.tracking || `Paquete #${paquete.id}`}</strong>
        </div>
        <EstadoBadge estado={paquete.estado_actual_detalle} />
      </div>
      <p className="text-secondary text-sm" style={{ marginBottom: '2px' }}>
        {paquete.tienda || 'Sin tienda registrada'} · {PAIS_LABEL[paquete.pais_origen] || paquete.pais_origen}
      </p>
      <p className="text-xs text-secondary" style={{ marginBottom: 0 }}>
        Prealertado el {new Date(paquete.fecha_prealerta).toLocaleDateString('es-EC')}
      </p>
    </div>
  );
}

function ConsolidadoCard({ grupo, onClick }) {
  return (
    <div className="card card--compact card--clickable" onClick={onClick}
      style={{ borderLeft: '3px solid var(--color-primary)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--spacing-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Layers size={16} style={{ color: 'var(--color-primary)' }} />
          <strong>{grupo.guia_agrupada}</strong>
        </div>
        <EstadoBadge estado={grupo.paquetes[0]?.estado_actual_detalle} />
      </div>
      <p className="text-secondary text-sm" style={{ marginBottom: '2px' }}>
        Paquete consolidado · {grupo.paquetes.length} paquetes
      </p>
      <p className="text-xs text-secondary" style={{ marginBottom: 0 }}>
        {grupo.paquetes.map((p) => p.tracking).join(', ')}
      </p>
    </div>
  );
}

function agruparPorConsolidacion(paquetes) {
  const sueltos = [];
  const grupos = {};
  for (const p of paquetes) {
    if (!p.consolidacion_detalle) {
      sueltos.push({ tipo: 'paquete', data: p });
    } else {
      const cid = p.consolidacion_detalle.id;
      if (!grupos[cid]) {
        grupos[cid] = {
          tipo: 'consolidado',
          consolidacion_id: cid,
          guia_agrupada: p.consolidacion_detalle.guia_agrupada,
          paquetes: [],
        };
      }
      grupos[cid].paquetes.push(p);
    }
  }
  return [...sueltos, ...Object.values(grupos)];
}

function LiquidacionDetalle({ liquidacion, abrirPago }) {
  const [mostrarPago, setMostrarPago] = useState(!!abrirPago);
  if (!liquidacion) return null;
  const lineas = liquidacion.lineas || [];
  const fleteLineas = lineas.filter((l) => l.tipo === 'flete');
  const servicioLineas = lineas.filter((l) => l.tipo === 'servicio');
  const reembolsoLineas = lineas.filter((l) => l.tipo === 'reembolso');

  const subtotal = [...fleteLineas, ...servicioLineas].reduce((s, l) => s + Number(l.base), 0);
  const iva = Number(liquidacion.total_iva || 0);
  const totalCourier = subtotal + iva;

  const ESTADO_LABEL = { pendiente: 'Pendiente', parcial: 'Parcial', pagado: 'Pagado' };
  const ESTADO_COLOR = { pendiente: '#f59e0b', parcial: '#3b82f6', pagado: '#22c55e' };

  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--spacing-4)', background: 'var(--color-bg)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-3)' }}>
        <div>
          <strong style={{ fontSize: '15px' }}>{liquidacion.guia_display}</strong>
          <div className="text-xs text-secondary">
            {new Date(liquidacion.fecha).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' })}
          </div>
        </div>
        <span style={{
          padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
          background: ESTADO_COLOR[liquidacion.estado_pago] + '20',
          color: ESTADO_COLOR[liquidacion.estado_pago],
        }}>
          {ESTADO_LABEL[liquidacion.estado_pago] || liquidacion.estado_pago}
        </span>
      </div>

      <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 'var(--spacing-2)' }}>
        {fleteLineas.map((l) => (
          <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
            <span className="text-sm">{l.concepto}</span>
            <span className="text-sm">${Number(l.base).toFixed(2)}</span>
          </div>
        ))}
        {servicioLineas.map((l) => (
          <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
            <span className="text-sm">{l.concepto}</span>
            <span className="text-sm">${Number(l.base).toFixed(2)}</span>
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid var(--color-border)', marginTop: '4px' }}>
          <strong className="text-sm">Subtotal</strong>
          <strong className="text-sm">${subtotal.toFixed(2)}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
          <span className="text-sm">IVA</span>
          <span className="text-sm">${iva.toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid var(--color-border)' }}>
          <strong className="text-sm">Total Courier</strong>
          <strong className="text-sm">${totalCourier.toFixed(2)}</strong>
        </div>

        {reembolsoLineas.map((l) => (
          <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
            <span className="text-sm">{l.concepto}</span>
            <span className="text-sm">${Number(l.base).toFixed(2)}</span>
          </div>
        ))}

        <div style={{
          display: 'flex', justifyContent: 'space-between', padding: '10px 12px', marginTop: '8px',
          background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius-md)', fontWeight: 700,
        }}>
          <span>Total a Pagar</span>
          <span style={{ color: 'var(--color-primary)', fontSize: '16px' }}>
            ${Number(liquidacion.total).toFixed(2)}
          </span>
        </div>

        {liquidacion.notas_cliente && (
          <p className="text-sm text-secondary" style={{ marginTop: 'var(--spacing-2)', fontStyle: 'italic' }}>
            {liquidacion.notas_cliente}
          </p>
        )}

        {Number(liquidacion.total_pagado) > 0 && (
          <p className="text-sm text-secondary" style={{ marginTop: 'var(--spacing-2)', marginBottom: 0 }}>
            <span>Pagado: ${Number(liquidacion.total_pagado).toFixed(2)}</span>
            {Number(liquidacion.saldo) > 0 && <span> · Saldo: <strong>${Number(liquidacion.saldo).toFixed(2)}</strong></span>}
          </p>
        )}

        {liquidacion.pagable && (
          <div style={{ marginTop: 'var(--spacing-3)' }}>
            {!mostrarPago ? (
              <button className="btn-primary" onClick={() => setMostrarPago(true)} style={{ width: '100%' }}>
                <CreditCard size={16} /> Pagar con tarjeta
              </button>
            ) : (
              <PagoPayphoneLiquidacion liquidacion={liquidacion} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ClientePanel() {
  const [paquetes, setPaquetes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [tab, setTab] = useState('activos');
  const [seleccionado, setSeleccionado] = useState(null);
  const [mostrarPrealerta, setMostrarPrealerta] = useState(false);
  const [mostrarPerfil, setMostrarPerfil] = useState(false);
  const [tieneCedula, setTieneCedula] = useState(true);
  const [detalleConsolidado, setDetalleConsolidado] = useState(null);
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [cargandoLiqs, setCargandoLiqs] = useState(false);
  const [pagarId, setPagarId] = useState(null);

  // `silencioso`: refresca la lista sin cambiarla por "Cargando..." (que desmonta todas las tarjetas).
  const cargar = (silencioso) => {
    if (silencioso !== true) setCargando(true);
    api.get('/paquetes/', { params: { archivado: false, ordering: '-creado' } })
      .then((res) => setPaquetes(res.data.results || res.data))
      .catch(() => toast.error('No se pudieron cargar tus paquetes.'))
      .finally(() => setCargando(false));
  };

  const cargarLiquidaciones = (silencioso) => {
    if (silencioso !== true) setCargandoLiqs(true);
    api.get('/liquidaciones/')
      .then((res) => setLiquidaciones(res.data.results || res.data))
      .catch(() => {})
      .finally(() => setCargandoLiqs(false));
  };

  useEffect(cargar, []);
  useEffect(cargarLiquidaciones, []);

  const recargarPerfil = () => {
    api.get('/mi-perfil/').then((res) => {
      setTieneCedula(Boolean(res.data.cliente?.cedula));
    }).catch(() => {});
  };

  useEffect(recargarPerfil, []);

  // Payphone redirige acá tras la Cajita de Pagos, pegando su propio
  // "?id=&clientTransactionId=" a la responseUrl SIN revisar si ya tenía un
  // "?" — por eso responseUrl no lleva query propio (ver PagoPayphoneCompraAsistida.js
  // / PagoPayphonePuertaPuerta.js) y el id de la solicitud viaja adentro del
  // clientTransactionId (`compra-<id>-<timestamp>` o `puerta-<id>-<timestamp>`,
  // según el servicio) en vez de en un parámetro aparte.
  // Confirmamos server-side antes de mostrar la pestaña, así no se ve el estado viejo.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payphoneId = params.get('id');
    const clientTransactionId = params.get('clientTransactionId');
    const matchCompra = clientTransactionId && clientTransactionId.match(/^compra-(\d+)-\d+$/);
    const matchPuerta = clientTransactionId && clientTransactionId.match(/^puerta-(\d+)-\d+$/);
    const matchLiq = clientTransactionId && clientTransactionId.match(/^liq-(\d+)-\d+$/);
    if (!payphoneId || (!matchCompra && !matchPuerta && !matchLiq)) return;
    window.history.replaceState({}, '', '/');

    const [endpoint, solicitudId, tabDestino, mensajeOk] = matchCompra
      ? ['compra-asistida', matchCompra[1], 'compra_asistida', '¡Pago confirmado! Ya podemos comprar tu artículo.']
      : matchLiq
        ? ['liquidaciones', matchLiq[1], 'liquidaciones', '¡Pago confirmado! Te enviamos el comprobante a tu correo.']
        : ['puerta-a-puerta', matchPuerta[1], 'puerta_puerta', '¡Pago confirmado! Ya podemos coordinar la recolección.'];

    api.post(`/${endpoint}/${solicitudId}/confirmar-pago-payphone/`, {
      id: payphoneId,
      client_transaction_id: clientTransactionId,
    })
      .then(() => toast.success(mensajeOk))
      .catch((err) => toast.error(err.response?.data?.error || 'No se pudo confirmar el pago.'))
      .finally(() => { setTab(tabDestino); if (matchLiq) cargarLiquidaciones(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Enlace del correo de la liquidación: /?pagar=<id> abre sus Liquidaciones con el pago a la vista.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('pagar');
    if (!id || !/^\d+$/.test(id)) return;
    window.history.replaceState({}, '', '/');
    setPagarId(Number(id));
    setTab('liquidaciones');
  }, []);

  // Enlaces de los correos de cotización: /?pagar_compra=<id> y /?pagar_puerta=<id> abren la
  // pestaña del servicio, donde la solicitud cotizada muestra su pago con tarjeta.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const destino = params.has('pagar_compra') ? 'compra_asistida' : params.has('pagar_puerta') ? 'puerta_puerta' : null;
    if (!destino) return;
    window.history.replaceState({}, '', '/');
    setTab(destino);
  }, []);

  const abrirPrealerta = () => {
    if (!tieneCedula) {
      toast.error('Completa tu cédula en tu perfil antes de prealertar un paquete.');
      setMostrarPerfil(true);
      return;
    }
    setMostrarPrealerta(true);
  };

  const { activos, pendientes, entregados } = useMemo(() => {
    const items = agruparPorConsolidacion(paquetes);

    const clasificar = (item) => {
      if (item.tipo === 'consolidado') {
        const primer = item.paquetes[0]?.estado_actual_detalle;
        if (primer?.codigo === 'entregado') return 'entregados';
        if (primer?.requiere_accion_cliente) return 'pendientes';
        return 'activos';
      }
      const p = item.data;
      if (p.estado_actual_detalle?.codigo === 'entregado') return 'entregados';
      if (p.estado_actual_detalle?.requiere_accion_cliente) return 'pendientes';
      return 'activos';
    };

    const result = { activos: [], pendientes: [], entregados: [] };
    for (const item of items) {
      result[clasificar(item)].push(item);
    }
    return result;
  }, [paquetes]);

  const listas = { activos, pendientes, entregados };
  const listaActual = listas[tab];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
        <div>
          <h2 style={{ marginBottom: '2px' }}>Mis paquetes</h2>
          {getCasillero() && <p className="text-secondary text-sm" style={{ marginBottom: 0 }}>Casillero {getCasillero()}</p>}
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-3)' }}>
          <button className="btn-secondary" onClick={() => setMostrarPerfil(true)}>
            <UserCircle size={16} /> Mi perfil
          </button>
          <button className="btn-primary" onClick={abrirPrealerta}>
            <Plus size={16} /> Nueva prealerta
          </button>
        </div>
      </div>

      <DashboardCliente />

      {!tieneCedula && (
        <p className="alert-warning" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertTriangle size={16} />
          Te falta completar tu cédula en el perfil — la necesitamos antes de que puedas prealertar un paquete.
          <button className="btn-ghost btn-sm" onClick={() => setMostrarPerfil(true)} style={{ marginLeft: 'auto' }}>
            Completar mi perfil
          </button>
        </p>
      )}

      <div className="tabs-nav">
        <button className={`tab-btn ${tab === 'activos' ? 'active' : ''}`} onClick={() => setTab('activos')}>
          <Package size={16} /> Activos ({activos.length})
        </button>
        <button className={`tab-btn ${tab === 'pendientes' ? 'active' : ''}`} onClick={() => setTab('pendientes')}>
          <Inbox size={16} /> Pendientes de acción ({pendientes.length})
        </button>
        <button className={`tab-btn ${tab === 'entregados' ? 'active' : ''}`} onClick={() => setTab('entregados')}>
          <CheckCircle2 size={16} /> Entregados ({entregados.length})
        </button>
        <button className={`tab-btn ${tab === 'liquidaciones' ? 'active' : ''}`} onClick={() => setTab('liquidaciones')}>
          <DollarSign size={16} /> Liquidaciones ({liquidaciones.length})
        </button>
        <button className={`tab-btn ${tab === 'compra_asistida' ? 'active' : ''}`} onClick={() => setTab('compra_asistida')}>
          <ShoppingBag size={16} /> Compra Asistida
        </button>
        <button className={`tab-btn ${tab === 'puerta_puerta' ? 'active' : ''}`} onClick={() => setTab('puerta_puerta')}>
          <Truck size={16} /> Puerta a Puerta
        </button>
      </div>

      {tab === 'compra_asistida' ? (
        <CompraAsistidaCliente />
      ) : tab === 'puerta_puerta' ? (
        <PuertaPuertaCliente />
      ) : tab === 'liquidaciones' ? (
        cargandoLiqs ? (
          <p className="text-secondary">Cargando...</p>
        ) : liquidaciones.length === 0 ? (
          <div className="empty-state">
            <p>No tienes liquidaciones todavía.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 'var(--spacing-4)' }}>
            {liquidaciones.map((liq) => (
              <LiquidacionDetalle key={liq.id} liquidacion={liq} abrirPago={pagarId === liq.id} />
            ))}
          </div>
        )
      ) : cargando ? (
        <p className="text-secondary">Cargando...</p>
      ) : listaActual.length === 0 ? (
        <div className="empty-state">
          <p>No tienes paquetes en esta categoría.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 'var(--spacing-4)' }}>
          {listaActual.map((item) => {
            if (item.tipo === 'consolidado') {
              return (
                <ConsolidadoCard
                  key={`cons-${item.consolidacion_id}`}
                  grupo={item}
                  onClick={() => setDetalleConsolidado({
                    guia_agrupada: item.guia_agrupada,
                    paquetes: item.paquetes,
                  })}
                />
              );
            }
            return (
              <PaqueteCard key={item.data.id} paquete={item.data} onClick={() => setSeleccionado(item.data)} />
            );
          })}
        </div>
      )}

      {seleccionado && <PaqueteDetalleModal paquete={seleccionado} onClose={() => setSeleccionado(null)} />}
      {detalleConsolidado && (
        <ConsolidadoDetalleModal
          consolidacion={detalleConsolidado}
          onClose={() => setDetalleConsolidado(null)}
        />
      )}
      {mostrarPerfil && (
        <PerfilModal onClose={() => { setMostrarPerfil(false); recargarPerfil(); }} />
      )}
      {mostrarPrealerta && (
        <PrealertaModal
          onClose={() => setMostrarPrealerta(false)}
          onCreado={() => { setMostrarPrealerta(false); cargar(true); }}
        />
      )}
    </div>
  );
}
